import { Accordion, Badge, Button, Col, Row } from "react-bootstrap";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { ProductImage } from "components/ui/images/product-image/product-image";
import { IEventStats, IProductInterest, ProductSignal } from "models/api/IClientProfile";
import { ClientEventType } from "models/api/IClientEvent";
import { IStoreProduct } from "models/api/IProduct";
import { useAppDispatch } from "store/hooks";
import { toggleProductModal } from "store/slices/uiSlice";

interface IProps {
    row: IProductInterest;
    product?: IStoreProduct;
}

const ShortDate = ({ value }: { value: string }) => <FormattedDate value={value} year={"2-digit"} month={"2-digit"} day={"2-digit"} />;

// Display order of the event types
const EVENT_TYPES: ClientEventType[] = ["PRODUCT_VIEWED", "PRODUCT_IMPRESSION"];

const SIGNAL_BADGES: Record<ProductSignal, { bg: string; className: string }> = {
    OPPORTUNITY: { bg: "warning", className: "text-dark" },
    WIN_BACK: { bg: "yellow", className: "text-dark" },
    INTEREST: { bg: "info", className: "" },
    NOTICED: { bg: "light", className: "text-muted border" },
    REGULAR: { bg: "light", className: "text-muted border" },
};

const StatTile = ({ labelKey, value, children }: { labelKey: string; value: number; children?: React.ReactNode }) => (
    <Col xs={12} sm={4}>
        <div className={"border rounded-3 p-3 h-100"}>
            <div className={"fs-xs text-muted"}>
                <FormattedMessage id={labelKey} />
            </div>
            <div className={"h5 mb-0"}>{value}</div>
            {children}
        </div>
    </Col>
);

const EventStatsTile = ({ type, stats }: { type: ClientEventType; stats: IEventStats }) => (
    <StatTile labelKey={`clientProfile.event.${type}`} value={stats.count}>
        <div className={"fs-xs text-muted"}>
            <FormattedMessage id={"clientProfile.eventDays"} values={{ count: stats.days }} />
            {" · "}
            <FormattedMessage id={"clientProfile.lastAt"} /> <ShortDate value={stats.lastOccurredAt} />
        </div>
        {stats.searchCount > 0 && (
            <div className={"fs-xs text-muted"}>
                <FormattedMessage id={"clientProfile.eventsInSearch"} values={{ count: stats.searchCount }} />
            </div>
        )}
    </StatTile>
);

const SignalBadge = ({ row }: { row: IProductInterest }) => {
    const badge = SIGNAL_BADGES[row.signal];
    return (
        <Badge pill bg={badge.bg} className={`fw-normal ${badge.className}`}>
            <FormattedMessage id={`clientProfile.signal.${row.signal}`} />
        </Badge>
    );
};

const OrderStatus = ({ row }: { row: IProductInterest }) => (
    <span className={"text-muted"}>
        {row.orderCount > 0 ? (
            <FormattedMessage id={"clientProfile.orderedTimes"} values={{ count: row.orderCount }} />
        ) : (
            <FormattedMessage id={"clientProfile.neverOrdered"} />
        )}
    </span>
);

const InterestSummary = ({ row }: { row: IProductInterest }) => {
    const intl = useIntl();
    const parts = EVENT_TYPES.flatMap((type) => {
        const stats = row.events[type];
        return stats ? [intl.formatMessage({ id: `clientProfile.summary.${type}` }, { count: stats.count, days: stats.days })] : [];
    });
    if (EVENT_TYPES.some((type) => (row.events[type]?.searchCount ?? 0) > 0)) {
        parts.push(intl.formatMessage({ id: "clientProfile.summary.inSearch" }));
    }
    return <>{parts.join(" · ")}</>;
};

export const ClientProfileProductItem = ({ row, product }: IProps) => {
    const dispatch = useAppDispatch();

    return (
        <Accordion.Item eventKey={String(row.productId)}>
            <Accordion.Header>
                <Row className={"align-items-center w-100 fs-sm gy-2"}>
                    <Col xs={12} md={5} className={"d-flex align-items-center gap-3"}>
                        <ProductImage id={row.productId} item={product} size={"md"} useZoom={false} />
                        <div>
                            <div>{product?.name ?? row.productId}</div>
                            <div className={"fs-xs text-muted"}>{product?.code}</div>
                        </div>
                    </Col>
                    <Col xs={12} md={4}>
                        <SignalBadge row={row} />
                        <div className={"fs-xs text-muted mt-1"}>
                            <InterestSummary row={row} />
                        </div>
                    </Col>
                    <Col xs={6} md={2}>
                        <OrderStatus row={row} />
                    </Col>
                    <Col xs={6} md={1} className={"text-end text-md-start fs-xs text-muted"}>
                        <ShortDate value={row.lastActivityAt} />
                    </Col>
                </Row>
            </Accordion.Header>
            <Accordion.Body>
                <Row className={"g-3"}>
                    {EVENT_TYPES.map((type) => {
                        const stats = row.events[type];
                        return stats && <EventStatsTile key={type} type={type} stats={stats} />;
                    })}
                    <StatTile labelKey={"clientProfile.orders"} value={row.orderCount}>
                        {row.lastOrderedAt && (
                            <div className={"fs-xs text-muted"}>
                                <FormattedMessage id={"clientProfile.lastAt"} /> <ShortDate value={row.lastOrderedAt} />
                            </div>
                        )}
                    </StatTile>
                </Row>
                {row.queries.length > 0 && (
                    <div className={"mt-4"}>
                        <div className={"fs-sm fw-semibold mb-2"}>
                            <FormattedMessage id={"clientProfile.foundWith"} />
                        </div>
                        <div className={"d-flex flex-wrap gap-2"}>
                            {row.queries.map((query) => (
                                <Badge key={query} pill bg={"secondary"} className={"text-dark fw-normal"}>
                                    {query}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}
                <div className={"d-flex justify-content-end mt-4"}>
                    {product && (
                        <Button variant={"outline-primary"} size={"sm"} onClick={() => dispatch(toggleProductModal({ product, hasParams: false }))}>
                            <FormattedMessage id={"clientProfile.openProduct"} />
                        </Button>
                    )}
                </div>
            </Accordion.Body>
        </Accordion.Item>
    );
};
